import { CloseIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import useApi from "../../../../shared/hooks/useApi";
import { useDebounce } from "../../../../shared/hooks/useDebounce";
import { getFullName } from "../../../../shared/utils/get-full-name";
import { BulkScheduleCaregiver } from "../bulk-schedule-tasks.types";

type Props = {
  isInvalid: boolean;
  value: BulkScheduleCaregiver | null;
  onChange: (caregiver: BulkScheduleCaregiver | null) => void;
};

/**
 * Search-as-you-type caregiver picker. The server validates that the chosen
 * caregiver holds a certification the task template allows.
 */
export default function BulkScheduleCaregiverSelect(props: Props) {
  const { queries } = useApi();
  const popover = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 300);

  const searchQuery = useQuery({
    ...queries.caregiver.search(debouncedSearchTerm),
    enabled: debouncedSearchTerm.length > 0 && popover.isOpen,
  });

  const caregivers = searchQuery.data?.caregivers ?? [];

  function handleInputChange(value: string) {
    setSearchTerm(value);

    if (value.length > 0) {
      popover.onOpen();
    } else {
      popover.onClose();
    }
  }

  function handleSelect(caregiver: BulkScheduleCaregiver) {
    props.onChange(caregiver);
    setSearchTerm("");
    popover.onClose();
  }

  function handleClear() {
    props.onChange(null);
    setSearchTerm("");
  }

  if (props.value !== null) {
    return (
      <InputGroup>
        <Input isReadOnly value={getFullName(props.value)} />
        <InputRightElement>
          <IconButton
            aria-label="Clear caregiver"
            icon={<CloseIcon fontSize={8} />}
            size="sm"
            variant="ghost"
            onClick={handleClear}
          />
        </InputRightElement>
      </InputGroup>
    );
  }

  return (
    <Popover matchWidth isOpen={popover.isOpen} placement="bottom-start" onClose={popover.onClose}>
      <PopoverTrigger>
        <Input
          isInvalid={props.isInvalid}
          placeholder="Search caregiver by name"
          value={searchTerm}
          onChange={(event) => handleInputChange(event.target.value)}
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody p={0}>
          {(() => {
            if (searchQuery.isFetching) {
              return (
                <Box p={4} textAlign="center">
                  <Spinner size="sm" />
                </Box>
              );
            }

            if (caregivers.length === 0) {
              return (
                <Box color="gray.500" p={4} textAlign="center">
                  {debouncedSearchTerm.length > 0 ? "No caregivers found" : "Type to search"}
                </Box>
              );
            }

            return (
              <List maxH="240px" overflowY="auto">
                {caregivers.map((caregiver) => (
                  <ListItem
                    key={caregiver.id}
                    _hover={{ bg: "gray.50" }}
                    cursor="pointer"
                    px={3}
                    py={2}
                    onClick={() => handleSelect(caregiver)}
                  >
                    <Flex align="center" gap={2} justify="space-between">
                      <Box>
                        <Text fontSize="sm" fontWeight="medium">
                          {getFullName(caregiver)}
                        </Text>
                        <Text color="gray.500" fontSize="xs" noOfLines={1}>
                          {caregiver.address ?? "No address"}
                        </Text>
                      </Box>
                      <Badge colorScheme={caregiver.status === "ACTIVE" ? "green" : "gray"}>
                        {caregiver.status}
                      </Badge>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            );
          })()}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
